/**
 * Auto-add the matching scented-oil product to the cart whenever a shopper
 * adds The 24/7 Room Diffuser to cart with an Aroma/Scent selected.
 *
 * IMPORTANT (fixed 2026-08-01, v10): sixth fix/change in a row for this
 * script. History of the previous fixes:
 *   v4 and earlier - triggered on every "change" of the Aroma radio picker,
 *     adding a companion oil for EACH scent ever selected, not just the
 *     final one.
 *   v5 - fixed that via a fragile "submit" event capture that didn't
 *     reliably fire.
 *   v6 - read the scent synchronously inside a wrapped `window.fetch`, but
 *     via `:checked` on the Aroma radio inputs, which are never actually
 *     checked when the picker renders as a dropdown.
 *   v7 - fixed the read to prefer the hidden `properties[Scent]` field
 *     the dropdown actually writes to.
 *   v8 - the diffuser's own add-to-cart click triggers a real page
 *     navigation to /cart right after its own /cart/add.js call succeeds,
 *     and the browser was aborting this script's own in-flight follow-up
 *     requests (checking the cart, then adding the oil) once that
 *     navigation started. Marked those requests `keepalive: true` so they
 *     survive navigation - the oil reliably ended up in the cart, but only
 *     after a short delay, so a shopper could land on /cart and briefly
 *     see only the diffuser before the oil appeared.
 *   v9 - fixed that ordering directly: the wrapped `window.fetch` no
 *     longer resolves the diffuser's own /cart/add.js call until the
 *     companion oil has ALSO finished being added (or a 4s safety timeout
 *     elapses). Since the theme's own code only redirects to /cart after
 *     that fetch resolves, this delays the redirect just long enough that
 *     the shopper never lands on an incomplete cart. Confirmed working:
 *     clicking straight through with no artificial delay and screenshotting
 *     the instant /cart loads shows both items already there.
 *
 * This version (v10) is a UX addition, not a bug fix: since v9 makes the
 * "Try At Home" click take a little longer (it now waits for the oil to be
 * added before redirecting), the button text is swapped to
 * "PREPARING CHECKOUT..." for the duration of that wait, so the extra time
 * reads as intentional progress rather than an unresponsive button. Text is
 * restored automatically if the add ever fails partway through (so the
 * button doesn't get stuck showing "PREPARING CHECKOUT..." with nothing
 * happening); on the success path it's simply left in place until the
 * theme's own redirect to /cart takes over.
 *
 * The Aroma picker on this store is rendered by a third-party product
 * customizer app. Its radio inputs have no "name" attribute and are not
 * reliably kept in sync with the visible selection when the picker renders
 * as a dropdown - the hidden `properties[Scent]` input is the one
 * authoritative source in that case, so it's checked first.
 *
 * Product lookup strategy:
 *   1. STATIC_SCENT_MAP below is a snapshot of every product currently
 *      tagged "single_notes_collection" in the catalog (built from the
 *      Admin API), keyed by exact product title. This is the primary
 *      lookup and works even for products that are set to "Unlisted"
 *      (e.g. Olympus Mint) - unlisted products don't appear in the
 *      storefront search index, so a live search alone would miss them.
 *   2. If a selected scent isn't in the map (e.g. a brand new product
 *      added after this script was last updated), it falls back to a
 *      live lookup: search /search/suggest.json for a product whose
 *      title exactly matches the scent label, then fetch that product's
 *      /products/<handle>.js to get its variant id. This only finds
 *      products that are Active + listed, so a newly added scent should
 *      be published normally (not left Unlisted) for the fallback to
 *      pick it up automatically.
 *
 * Snapshot taken 2026-07-31. Includes a few scents not yet shown in the
 * live picker (Yarra Eucalyptus, Kerala Ginger, Sambava Vanilla, Colombo
 * Coconut) so they'll already work the moment they're added to the
 * picker - no script change needed for those. If new scents are added
 * to the catalog *after* this snapshot, the live fallback should still
 * catch them as long as they're published/listed normally.
 *
 * Note on pricing: this script adds the oil at its normal price. Per the
 * merchant, an existing discount rule in Shopify's back office already
 * handles making the first bottle free ("+ Your first FREE Aroma") - this
 * script does not touch price/discounting.
 *
 * Note on scope: the /cart/add interception below acts on ANY POST to
 * /cart/add(.js) made while a scent selection is present on the page -
 * this is intentional and correct for this product page today (the
 * diffuser's own "Add to cart" is the only such call here). If a
 * "frequently bought together" or similar widget is ever added to this
 * page that also posts to /cart/add.js for a *different* product, this
 * script would also (incorrectly) attach the oil to that add, AND would
 * delay that add's own resolution and swap its button text the same way -
 * revisit this scoping if that ever changes.
 */
(function () {
  var RADIO_ID_PREFIX = 'tcustomizer-form-field-';
  var HIDDEN_SCENT_INPUT_SELECTOR = 'input[type="hidden"][name="properties[Scent]"]';
  var QUANTITY = 1;
  var COMPANION_ADD_TIMEOUT_MS = 4000;
  var LOADING_BUTTON_TEXT = 'PREPARING CHECKOUT...';
  var CTA_BUTTON_TEXT_PATTERN = /^try at home$/i;

  var STATIC_SCENT_MAP = {
    'Provence Lavender (300ml)': 49249343963482,
    'Kyoto Jasmine (300ml)': 49252717101402,
    'Marrakesh Orange (300ml)': 49252743414106,
    'Sicilian Lemon (300ml)': 49275064484186,
    'Olympus Mint (300ml)': 49275072643418,
    'Comisa Rosemary (300ml)': 49275077067098,
    'Yarra Eucalyptus (300ml)': 49275099349338,
    'Kerala Ginger (300ml)': 49275125104986,
    'Sambava Vanilla (300ml)': 53688173723994,
    'Colombo Coconut (300ml)': 53688189878618
  };

  var lookupCache = {}; // scent label -> variant id (or null if not found)
  var inFlight = {}; // scent label -> true while an add is in progress
  var isCompanionRequest = false; // true only while WE are calling /cart/add.js ourselves
  var loadingCtaButtons = null; // Array<{el, originalText}> while a CTA click is in flight

  function getCurrentlySelectedScent() {
    // Primary source: the hidden field the customizer app itself writes to
    // and that the add-to-cart request actually reads from. This is kept
    // in sync no matter how the picker is visually rendered (dropdown or
    // swatch list), so it's the most reliable signal.
    var hiddenInput = document.querySelector(HIDDEN_SCENT_INPUT_SELECTOR);
    if (hiddenInput && hiddenInput.value) {
      return hiddenInput.value;
    }

    // Fallback: some render modes of the picker use clickable swatches
    // that DO set the underlying radio's checked state.
    var checked = document.querySelector(
      'input[type="radio"][id^="' + RADIO_ID_PREFIX + '"]:checked'
    );
    return checked ? checked.value : null;
  }

  // Swaps the "Try At Home" button(s) to a loading label for the duration
  // of the add-to-cart + companion-add sequence, so the extra time v9
  // introduces (waiting for the oil before redirecting) reads as visible
  // progress instead of an unresponsive click. There can be more than one
  // matching button on the page (e.g. a sticky mobile bar), so all of them
  // are updated together.
  function showCtaLoadingState() {
    if (loadingCtaButtons) return; // already showing
    loadingCtaButtons = Array.prototype.filter
      .call(document.querySelectorAll('button'), function (b) {
        return CTA_BUTTON_TEXT_PATTERN.test((b.textContent || '').trim());
      })
      .map(function (el) {
        return { el: el, originalText: el.textContent };
      });
    loadingCtaButtons.forEach(function (b) {
      b.el.textContent = LOADING_BUTTON_TEXT;
    });
  }

  // Restores the original button label. Only needed when the add-to-cart
  // sequence does NOT end in a redirect (e.g. the diffuser add itself
  // failed) - on the success path the page navigates to /cart before this
  // would ever be needed, so the button is simply left showing the loading
  // label until then.
  function restoreCtaButtons() {
    if (!loadingCtaButtons) return;
    loadingCtaButtons.forEach(function (b) {
      b.el.textContent = b.originalText;
    });
    loadingCtaButtons = null;
  }

  function isAlreadyInCart(variantId) {
    return fetch('/cart.js', { keepalive: true })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        return cart.items.some(function (item) { return item.variant_id === variantId; });
      })
      .catch(function (err) {
        console.error('[aroma-auto-add] could not read cart', err);
        return false;
      });
  }

  function findVariantIdForScent(scentLabel) {
    if (Object.prototype.hasOwnProperty.call(STATIC_SCENT_MAP, scentLabel)) {
      return Promise.resolve(STATIC_SCENT_MAP[scentLabel]);
    }

    if (Object.prototype.hasOwnProperty.call(lookupCache, scentLabel)) {
      return Promise.resolve(lookupCache[scentLabel]);
    }

    var searchUrl =
      '/search/suggest.json?q=' +
      encodeURIComponent(scentLabel) +
      '&resources[type]=product&resources[limit]=5&resources[options][fields]=title';

    return fetch(searchUrl, { keepalive: true })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var products =
          (data.resources && data.resources.results && data.resources.results.products) || [];
        var match =
          products.filter(function (p) { return p.title === scentLabel; })[0] || products[0];

        if (!match) {
          console.warn('[aroma-auto-add] no matching product found for scent:', scentLabel);
          lookupCache[scentLabel] = null;
          return null;
        }

        return fetch('/products/' + match.handle + '.js', { keepalive: true })
          .then(function (res) { return res.json(); })
          .then(function (product) {
            var variant = (product.variants && product.variants[0]) || null;
            var variantId = variant ? variant.id : null;
            lookupCache[scentLabel] = variantId;
            return variantId;
          });
      })
      .catch(function (err) {
        console.error('[aroma-auto-add] lookup failed for', scentLabel, err);
        return null;
      });
  }

  // Returns a promise that ALWAYS resolves (never rejects), so callers can
  // safely wait on it without needing their own catch. Resolves once the
  // companion oil has been added (or once it's determined there's nothing
  // to do / nothing that can be done).
  function addCompanionProduct(scentLabel) {
    if (inFlight[scentLabel]) return Promise.resolve();
    inFlight[scentLabel] = true;

    return findVariantIdForScent(scentLabel).then(function (variantId) {
      if (!variantId) {
        inFlight[scentLabel] = false;
        return;
      }

      return isAlreadyInCart(variantId).then(function (alreadyThere) {
        if (alreadyThere) {
          console.log('[aroma-auto-add] companion already in cart, skipping:', scentLabel);
          inFlight[scentLabel] = false;
          return;
        }

        isCompanionRequest = true;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: QUANTITY,
                properties: { _auto_added: 'Matched ' + scentLabel + ' aroma selection' }
              }
            ]
          })
        })
          .then(function (response) {
            isCompanionRequest = false;
            inFlight[scentLabel] = false;
            if (!response.ok) {
              return response.json().then(function (err) {
                console.error('[aroma-auto-add] failed to add companion product (likely out of stock or unavailable):', scentLabel, err);
              });
            }
            console.log('[aroma-auto-add] companion product added:', scentLabel);

            ['cart:refresh', 'cart:updated', 'cart:build'].forEach(function (name) {
              document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
            });
          })
          .catch(function (err) {
            isCompanionRequest = false;
            inFlight[scentLabel] = false;
            console.error('[aroma-auto-add] network error while adding companion product', err);
          });
      });
    });
  }

  // Wait for `promise` to settle, but never longer than `ms` - if it's
  // still pending after that, give up waiting (the underlying fetch calls
  // are keepalive, so they'll still complete in the background; we just
  // stop blocking on them). Always resolves, never rejects.
  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (!settled) {
          settled = true;
          resolve();
        }
      }, ms);

      promise.then(
        function () {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve();
          }
        },
        function () {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve();
          }
        }
      );
    });
  }

  // Only act on a REAL /cart/add(.js) POST request - i.e. only when the
  // shopper actually clicks "Add to cart" and the theme's own JS issues the
  // request, never on a plain scent selection (which never touches the
  // cart by itself). We read whichever scent is currently selected at this
  // exact instant, so switching scents any number of times beforehand has
  // no effect - only the selection at the moment of the real add-to-cart
  // call is used.
  //
  // The promise returned here is what the theme's own code awaits before
  // it redirects to /cart - so it isn't resolved until the companion oil
  // has finished being added (or the safety timeout elapses). That's what
  // prevents the shopper from ever landing on /cart before the oil is
  // actually there. The CTA button's label is swapped to a loading state
  // for that same span, and restored if the sequence doesn't end in a
  // redirect.
  var nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var isCartAdd = method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url);

    if (isCartAdd && !isCompanionRequest) {
      var scent = getCurrentlySelectedScent();
      showCtaLoadingState();
      return nativeFetch(input, init)
        .then(function (response) {
          if (scent && response.ok) {
            return withTimeout(addCompanionProduct(scent), COMPANION_ADD_TIMEOUT_MS).then(function () {
              return response;
            });
          }
          restoreCtaButtons();
          return response;
        })
        .catch(function (err) {
          restoreCtaButtons();
          throw err;
        });
    }

    return nativeFetch(input, init);
  };
})();
